<template>
  <div class="c2c-sel" ref="rootEl">
    <button
      :id="inputId"
      ref="buttonEl"
      type="button"
      class="c2c-sel-btn"
      :class="{ 'c2c-sel-btn--open': open }"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-controls="listId"
      :aria-activedescendant="open && activeIndex >= 0 ? optionId(activeIndex) : undefined"
      @click="toggle"
      @keydown="onButtonKey"
    >
      <span class="c2c-sel-value">{{ selectedLabel }}</span>
      <ChevronDown class="c2c-sel-caret" :size="18" aria-hidden="true" />
    </button>

    <ul v-if="open" :id="listId" class="c2c-sel-list" role="listbox" :aria-label="label">
      <li
        v-for="(opt, i) in options"
        :key="opt.value"
        :id="optionId(i)"
        class="c2c-sel-opt"
        :class="{
          'c2c-sel-opt--active': i === activeIndex,
          'c2c-sel-opt--sel': opt.value === modelValue
        }"
        role="option"
        :aria-selected="opt.value === modelValue"
        @mousedown.prevent="choose(opt.value)"
        @mousemove="activeIndex = i"
      >
        <Check class="c2c-sel-tick" :size="16" aria-hidden="true" />
        <span>{{ opt.label }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
/**
 * A select that we can actually style.
 *
 * The native control's dropdown list is drawn by the operating system and
 * cannot be styled, which is why the one on this form looked like a Windows
 * 98 listbox next to everything around it. This follows the ARIA
 * combobox-with-listbox pattern instead: a button that owns the value, a
 * listbox that only exists while open, and the keyboard behaviour people
 * expect from the native control - Up/Down, Home/End, Enter, Escape, and
 * type-ahead on letters.
 *
 * Closing on blur rather than on click-outside keeps it honest when the
 * popup is dismissed by tabbing away.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue"
import { Check, ChevronDown } from "lucide-vue-next"

export interface SelectOption { value: string; label: string }

const props = defineProps<{
  modelValue: string
  options: SelectOption[]
  label: string
  inputId?: string
}>()
const emit = defineEmits<{ "update:modelValue": [string] }>()

const open = ref(false)
const activeIndex = ref(-1)
const rootEl = ref<HTMLElement | null>(null)
const buttonEl = ref<HTMLButtonElement | null>(null)

const uid = Math.random().toString(36).slice(2, 8)
const listId = "c2c-sel-list-" + uid
const optionId = (i: number) => "c2c-sel-opt-" + uid + "-" + i

const selectedIndex = computed(() => props.options.findIndex(o => o.value === props.modelValue))
const selectedLabel = computed(() => props.options[selectedIndex.value]?.label ?? "")

function openList() {
  if (open.value) return
  open.value = true
  activeIndex.value = selectedIndex.value >= 0 ? selectedIndex.value : 0
  document.addEventListener("pointerdown", onOutside, true)
  nextTick(scrollActiveIntoView)
}

function close(refocus = true) {
  if (!open.value) return
  open.value = false
  document.removeEventListener("pointerdown", onOutside, true)
  if (refocus) buttonEl.value?.focus()
}

function toggle() { open.value ? close() : openList() }

function onOutside(e: Event) {
  if (!rootEl.value?.contains(e.target as Node)) close(false)
}
onBeforeUnmount(() => close(false))

function choose(value: string) {
  if (value !== props.modelValue) emit("update:modelValue", value)
  close()
}

function move(delta: number) {
  const n = props.options.length
  if (!n) return
  if (!open.value) { openList(); return }
  activeIndex.value = (activeIndex.value + delta + n) % n
  scrollActiveIntoView()
}

/** Type-ahead: jump to the next option whose label starts with the letter. */
let typed = ""
let typedTimer: ReturnType<typeof setTimeout> | undefined
function typeAhead(ch: string) {
  clearTimeout(typedTimer)
  typed += ch.toLowerCase()
  typedTimer = setTimeout(() => { typed = "" }, 600)
  const from = Math.max(0, activeIndex.value)
  const n = props.options.length
  for (let i = 1; i <= n; i++) {
    const idx = (from + i) % n
    if (props.options[idx].label.toLowerCase().startsWith(typed)) {
      if (!open.value) { choose(props.options[idx].value); return }
      activeIndex.value = idx
      scrollActiveIntoView()
      return
    }
  }
}

function onButtonKey(e: KeyboardEvent) {
  switch (e.key) {
    case "ArrowDown": e.preventDefault(); move(1); break
    case "ArrowUp": e.preventDefault(); move(-1); break
    case "Home": if (open.value) { e.preventDefault(); activeIndex.value = 0; scrollActiveIntoView() } break
    case "End": if (open.value) { e.preventDefault(); activeIndex.value = props.options.length - 1; scrollActiveIntoView() } break
    case "Enter":
    case " ":
      e.preventDefault()
      if (open.value && activeIndex.value >= 0) choose(props.options[activeIndex.value].value)
      else openList()
      break
    case "Escape": if (open.value) { e.preventDefault(); close() } break
    case "Tab": close(false); break
    default:
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) typeAhead(e.key)
  }
}

function scrollActiveIntoView() {
  const el = document.getElementById(optionId(activeIndex.value))
  el?.scrollIntoView({ block: "nearest" })
}

// A value changed from outside while the list is open should move the
// highlight with it rather than leaving it pointing at the old row.
watch(() => props.modelValue, () => {
  if (open.value && selectedIndex.value >= 0) activeIndex.value = selectedIndex.value
})
</script>

<style scoped>
.c2c-sel { position: relative; }

.c2c-sel-btn {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  width: 100%; box-sizing: border-box;
  min-height: 44px; padding: 10px 12px;
  font: inherit; font-size: 16px; text-align: start;
  color: #202122; background: #fff;
  border: 1px solid #a2a9b1; border-radius: 6px;
  cursor: pointer;
  transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
}
.c2c-sel-btn:hover { border-color: #72777d; }
.c2c-sel-btn:focus-visible {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-sel-btn--open {
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-sel-value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.c2c-sel-caret { flex: 0 0 auto; color: #54595d; transition: transform 140ms ease-out; }
.c2c-sel-btn--open .c2c-sel-caret { transform: rotate(180deg); }

.c2c-sel-list {
  position: absolute; z-index: 30; top: calc(100% + 4px); inset-inline: 0;
  margin: 0; padding: 4px; list-style: none;
  max-height: 260px; overflow-y: auto;
  background: #fff;
  border: 1px solid #c8ccd1; border-radius: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.08);
}
.c2c-sel-opt {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 10px; border-radius: 6px;
  font-size: 14px; color: #202122; cursor: pointer;
}
.c2c-sel-opt--active { background: #eaf3ff; }
.c2c-sel-opt--sel { font-weight: 600; }
/* The tick holds its column whether or not it is shown, so labels do not
   shift sideways as the selection moves. */
.c2c-sel-tick { flex: 0 0 auto; color: #3366cc; visibility: hidden; }
.c2c-sel-opt--sel .c2c-sel-tick { visibility: visible; }
</style>
